import { saveAs } from "file-saver";
import JSZip from "jszip";
import { POST_CONSULT_STATUSES } from "./PostConsultSummaryUtils";

const palette = { navy: "183B56", blue: "EAF2F8", teal: "DDF0EA", red: "FCE4E4", white: "FFFFFF" };

export const buildPostConsultWorkbook = async (XlsxPopulate, summary) => {
  const isFull = summary.mode === "full";
  const workbook = await XlsxPopulate.fromBlankAsync();
  const scope = `Node: ${summary.nodeLabel} | Customer: ${summary.customer || "All Customers"}`;
  const dates = `Latest sleep study: ${summary.startDate} to ${summary.endDate} | Checked: ${summary.asOf} (Bangkok)`;
  const totalUnassigned = (people) => (people.find((person) => person.name === "Not assigned") || {}).count || 0;
  const statuses = Array.from(new Set(["Doctor Scored", "Post-Consult", ...summary.statuses.map((row) => row.status)]));
  const overview = workbook.sheet(0).name("Overview");
  const overviewRows = [
    [`Post Consult Summary — ${isFull ? "Full" : "Short"}`],
    ["Latest Sleep Study Date", `${summary.startDate} to ${summary.endDate} (inclusive)`],
    ["Node", summary.nodeLabel], ["Customer", summary.customer || "All Customers"],
    ["Checked on (Bangkok)", summary.asOf],
    ["Case scope", "All statuses except Cancel and Failure. Cases without HN are excluded."], [],
    ["Summary", "Cases"], ["Total Cases", summary.total], ["Overdue Cases (>45 days)", summary.overdue.length],
    ["Post Consult Tech — Not assigned", totalUnassigned(summary.techs)],
    ["Post Consult Doctor — Not assigned", totalUnassigned(summary.doctors)], [],
    ["Sheet guide", "What it shows"],
    ["Tech Summary", isFull ? "One row per post-consult tech: total cases, overdue cases, and current status counts." : "One row per post-consult tech with a total case count. No status breakdown."],
    ["Doctor Summary", isFull ? "One row per post-consult doctor: total cases, overdue cases, and current status counts." : "One row per post-consult doctor with a total case count. No status breakdown."],
    ["Overdue Cases", "Only cases more than 45 days after their latest sleep study date, in the eight overdue statuses."],
    ["All Cases", "All cases behind the counts. Use column filters to find an HN, order, person, or status."], [],
    ["Counting rules", `One sleep study order counts as one case. ${isFull ? "Status columns add up to Total Cases. " : "Tech and doctor totals use the same cases as Full. "}Overdue is a subset of Total Cases, not an additional count.`],
    ["Overdue statuses", POST_CONSULT_STATUSES.join(", ")],
    ["Assignments", "Counts use current post-consult assignments and current case statuses. Not assigned is included."], [],
    ...(isFull ? [["Current Status", "Cases"], ...summary.statuses.map(({ status, count }) => [status, count]), ["TOTAL", summary.total]] : []),
  ];
  overview.cell("A1").value(overviewRows.map((row) => [row[0] === undefined ? null : row[0], row[1] === undefined ? null : row[1]]));
  overview.usedRange().style({ fontFamily: "Calibri", fontSize: 11, verticalAlignment: "center", wrapText: true });
  overview.column("A").width(38); overview.column("B").width(92);
  overview.range("A1:B1").merged(true).style({ fill: palette.navy, fontColor: palette.white, bold: true, fontSize: 18 });
  overview.row(1).height(36);
  (isFull ? [8, 14, 24] : [8, 14]).forEach((row) => {
    overview.range(row, 1, row, 2).style({ fill: palette.navy, fontColor: palette.white, bold: true });
    overview.row(row).height(26);
  });
  [6, 15, 16, 17, 18, 20, 22].forEach((row) => overview.row(row).height(34));
  overview.row(21).height(62);
  overview.range("A9:B12").style({ fill: palette.blue });
  overview.range("A10:B10").style({ fill: palette.red, bold: true });
  overview.range("B9:B12").style({ numberFormat: "0", bold: true, horizontalAlignment: "right" });
  if (isFull) overview.range(overviewRows.length, 1, overviewRows.length, 2).style({ fill: palette.teal, bold: true });
  overview.freezePanes(0, 1);
  overview.tabColor(palette.navy);

  const tableSheet = (name, headers, rows, widths, options = {}) => {
    const sheet = workbook.addSheet(name);
    const lastColumn = headers.length;
    sheet.cell("A1").value(name);
    sheet.range(1, 1, 1, lastColumn).merged(true).style({ fill: palette.navy, fontColor: palette.white, bold: true, fontSize: 16 });
    sheet.cell("A2").value(scope);
    sheet.range(2, 1, 2, lastColumn).merged(true);
    sheet.cell("A3").value(dates);
    sheet.range(3, 1, 3, lastColumn).merged(true);
    sheet.cell("A5").value([headers, ...rows]);
    sheet.usedRange().style({ fontFamily: "Calibri", fontSize: 11, verticalAlignment: "center", wrapText: true });
    sheet.cell("A1").style("fontSize", 16);
    sheet.row(1).height(32); sheet.row(2).height(26); sheet.row(3).height(26); sheet.row(5).height(46);
    sheet.range(5, 1, 5, lastColumn).style({ fill: palette.navy, fontColor: palette.white, bold: true });
    widths.forEach((width, index) => sheet.column(index + 1).width(width));
    rows.forEach((_, index) => {
      const rowNumber = index + 6;
      sheet.row(rowNumber).height(options.matrix ? 27 : 38);
      if (index % 2 === 0) sheet.range(rowNumber, 1, rowNumber, lastColumn).style("fill", "F2F6FA");
    });
    if (rows.length) {
      (options.numericColumns || []).forEach((column) => sheet.range(6, column, rows.length + 5, column)
        .style({ numberFormat: "0", horizontalAlignment: "right" }));
      if (options.overdueColumn) rows.forEach((row, index) => {
        if (row[options.overdueColumn - 1] > 0) sheet.cell(index + 6, options.overdueColumn).style({ fill: palette.red, bold: true });
      });
    }
    sheet.autoFilter(sheet.range(5, 1, rows.length + 5, lastColumn));
    sheet.freezePanes(options.matrix ? 1 : 2, 5);
    sheet.tabColor(options.matrix ? "287C8E" : "67849A");
    return sheet;
  };

  const personSheet = (name, people, field) => {
    if (!isFull) {
      const rows = people.map((person) => [person.name, person.count]);
      const headers = [name === "Tech Summary" ? "Post Consult Tech" : "Post Consult Doctor", "Total Cases"];
      const sheet = tableSheet(name, headers, rows, [55, 32], { matrix: true, numericColumns: [2] });
      sheet.row(2).height(40); sheet.row(3).height(40);
      const totalRow = rows.length + 6;
      sheet.cell(totalRow, 1).value([["TOTAL", summary.total]]);
      sheet.range(totalRow, 1, totalRow, 2).style({ fill: palette.teal, bold: true, numberFormat: "0" });
      sheet.row(totalRow).height(28);
      return;
    }
    const overdueByPerson = new Map();
    summary.overdue.forEach((row) => overdueByPerson.set(row[field], (overdueByPerson.get(row[field]) || 0) + 1));
    const headers = [name === "Tech Summary" ? "Post Consult Tech" : "Post Consult Doctor", "Total Cases", "Overdue Cases", ...statuses];
    const rows = people.map((person) => {
      const byStatus = new Map(person.statuses.map((row) => [row.status, row.count]));
      return [person.name, person.count, overdueByPerson.get(person.name) || 0, ...statuses.map((status) => byStatus.get(status) || 0)];
    });
    const sheet = tableSheet(name, headers, rows, [30, 14, 16, ...statuses.map(() => 21)], {
      matrix: true, overdueColumn: 3, numericColumns: headers.slice(1).map((_, index) => index + 2),
    });
    const totals = ["TOTAL", summary.total, summary.overdue.length, ...statuses.map((status) =>
      (summary.statuses.find((row) => row.status === status) || {}).count || 0)];
    const totalRow = rows.length + 6;
    sheet.cell(totalRow, 1).value([totals]);
    sheet.range(totalRow, 1, totalRow, headers.length).style({ fill: palette.teal, bold: true, numberFormat: "0" });
    sheet.row(totalRow).height(28);
  };
  personSheet("Tech Summary", summary.techs, "tech");
  personSheet("Doctor Summary", summary.doctors, "doctor");

  const overdueKeys = new Set(summary.overdue.map((row) => row.key));
  const headers = ["Order", "HN", "Patient", "Node", "Customer", "Latest Sleep Study Date", "Current Status", "Post Consult Tech", "Post Consult Doctor", "Days Since Study", "Overdue?", "Days Overdue (>45)"];
  const caseRows = (cases) => cases.map((row) => [row.order, row.hn, row.patient, row.node, row.customer, row.studyDate, row.status,
    row.tech, row.doctor, row.daysSinceStudy, overdueKeys.has(row.key) ? "Yes" : "No", overdueKeys.has(row.key) ? row.overdueDays : 0]);
  [ ["Overdue Cases", summary.overdue], ["All Cases", summary.cases] ].forEach(([name, cases]) => {
    tableSheet(name, headers, caseRows(cases), [23, 17, 30, 25, 25, 19, 24, 27, 27, 16, 13, 19], {
      overdueColumn: 12, numericColumns: [10, 12],
    });
  });
  return workbook;
};

export const outputPostConsultWorkbook = async (workbook, type = "blob") => {
  // xlsx-populate has no public page-setup API. Set print layout in its generated
  // worksheet XML so wide status tables do not split horizontally when printed.
  const zip = await JSZip.loadAsync(await workbook.outputAsync({ type: "uint8array" }));
  await Promise.all(workbook.sheets().map(async (_, index) => {
    const path = `xl/worksheets/sheet${index + 1}.xml`;
    const xml = await zip.file(path).async("string");
    const pageSetup = '<pageSetup paperSize="9" orientation="landscape" fitToWidth="1" fitToHeight="0"/>';
    const withPageSetup = /<pageSetup\b/.test(xml)
      ? xml.replace(/<pageSetup\b[^>]*\/>/, pageSetup)
      : xml.replace("</worksheet>", `${pageSetup}</worksheet>`);
    zip.file(path, withPageSetup
      .replace("</sheetPr>", '<pageSetUpPr fitToPage="1"/></sheetPr>'));
  }));
  return zip.generateAsync({ type, compression: "DEFLATE", mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
};

export const exportPostConsultWorkbook = async (summary) => {
  // Let the app minify the browser bundle once. Re-minifying the vendor's .min.js
  // with this toolchain corrupts its embedded blank workbook (an empty ZIP).
  const module = await import("xlsx-populate/browser/xlsx-populate-no-encryption.js");
  const workbook = await buildPostConsultWorkbook(module.default || module, summary);
  const blob = await outputPostConsultWorkbook(workbook);
  saveAs(blob, `Post_Consult_Summary_${summary.mode}_${summary.startDate}_${summary.endDate}.xlsx`);
};
